import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { KpiCard } from "@/features/reports/KpiCard";
import { usePolicies, useClaims } from "@/features/insurance/api";
import { useLookups } from "@/features/assets/api";
import { policyStatus, daysToExpiry, formatINR } from "@/features/insurance/types";
import type { ReportFilters } from "@/features/reports/types";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/reports/insurance-register")({
  component: InsuranceRegisterReport,
});

function isPropertyPolicy(p: { coverage_scope?: string | null; coverage_mode?: string | null }) {
  return p.coverage_mode === "Property" || p.coverage_scope === "Building";
}

function InsuranceRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  const { data: policies = [], isLoading } = usePolicies({}, currentBranchId);
  const { data: claims = [] } = useClaims({}, currentBranchId);
  const { data: lookups } = useLookups();
  const branchName = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of lookups?.branches ?? []) map.set(b.id, b.name);
    return (id: string | null | undefined) => (id ? map.get(id) ?? "—" : "—");
  }, [lookups]);

  let active = 0, sumInsured = 0;
  for (const p of policies) if (policyStatus(p) === "Active") { active++; sumInsured += Number(p.sum_insured || 0); }
  const openClaims = claims.filter((c) => !["Settled", "Partially Settled", "Rejected"].includes(c.status)).length;

  const exportRows = useMemo(() => () => policies.map((p) => ({
    Policy: p.policy_no, Type: p.type, Insurer: p.insurer_name, Broker: p.broker ?? "",
    Start: p.start_date, End: p.end_date, SumInsured: p.sum_insured, Premium: p.premium,
    Status: policyStatus(p),
    CoverageScope: p.coverage_scope ?? "",
    CoverageMode: p.coverage_mode ?? "",
    CoveredAssets: p.asset_count ?? 0,
    PropertyCoverage: isPropertyPolicy(p) ? "Yes" : "No",
    Branch: branchName(p.branch_id),
  })), [policies, branchName]);

  return (
    <ReportShell id="R-31" title="Insurance Policy Register"
      description="Policies, sum insured, premium and claims."
      filters={filters} onChange={setFilters} enabled={[]} exportRows={exportRows}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Active Policies" value={active} tone="success" />
        <KpiCard label="Total Sum Insured" value={formatINR(sumInsured)} />
        <KpiCard label="Total Policies" value={policies.length} />
        <KpiCard label="Open Claims" value={openClaims} tone={openClaims > 0 ? "warning" : "default"} />
      </div>
      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Policy #</TableHead><TableHead>Type</TableHead><TableHead>Insurer</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead className="text-right">Covered</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead className="text-right">Sum Insured</TableHead>
              <TableHead className="text-right">Premium</TableHead>
              <TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {policies.map((p) => {
                const s = policyStatus(p);
                const d = daysToExpiry(p.end_date);
                const prop = isPropertyPolicy(p);
                const covered = p.asset_count ?? 0;
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.policy_no}</TableCell>
                    <TableCell>{p.type}</TableCell>
                    <TableCell>{p.insurer_name}</TableCell>
                    <TableCell className="text-xs">{p.start_date} → {p.end_date}{s === "Active" && d <= 30 && <span className="ml-1 text-amber-600">({d}d left)</span>}</TableCell>
                    <TableCell className="text-xs"><Badge variant="secondary">{p.coverage_scope ?? "—"}</Badge></TableCell>
                    <TableCell className="text-xs">{p.coverage_mode ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {prop && covered === 0 ? <span className="text-muted-foreground">—</span> : covered}
                    </TableCell>
                    <TableCell>{prop ? <Badge variant="outline">Yes</Badge> : <span className="text-xs text-muted-foreground">No</span>}</TableCell>
                    <TableCell className="text-xs">{branchName(p.branch_id)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatINR(p.sum_insured)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatINR(p.premium)}</TableCell>
                    <TableCell><Badge variant={s === "Active" ? "default" : "secondary"}>{s}</Badge></TableCell>
                  </TableRow>
                );
              })}
              {policies.length === 0 && <TableRow><TableCell colSpan={12} className="py-8 text-center text-sm text-muted-foreground">No policies.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </ReportShell>
  );
}
